import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    getSyntheticGoalPriorityFromActual,
    MoneyMapRulePriority,
} from '@fruitful/money-map';
import type {
    DeepPartial,
    MoneyMapRuleGroup as MoneyMapRuleGroupType,
    MoneyMapRuleWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { BodyMediumRegular, BodySmallRegular, XStack, YStack } from 'smoothie';

import { DragHandle } from './DragHandle';
import { DRAG_PINK, GOAL_DRAG_STROKE_PX } from './goalsWaterfallConstants';
import type { DragHandleProps, DropIndicator } from './goalsWaterfallTypes';
import { GroupGoalRow } from './GroupGoalRow';
import { SortableGroupGoal } from './SortableGroupGoal';

interface GoalGroupSectionProps {
    group: DeepPartial<MoneyMapRuleGroupType>;
    groupRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    showOutline?: boolean;
    isDragOverlay?: boolean;
    dropIndicator: DropIndicator;
    dragHandleProps?: DragHandleProps;
    selectedGoalId?: string | null;
    onPressGroupHeader?: () => void;
    onPressGoal?: (goalId: string) => void;
    onPressGoalPercentage?: (goalId: string) => void;
}

export const GoalGroupSection = ({
    group,
    groupRules,
    moneyMap,
    showOutline = false,
    isDragOverlay = false,
    dropIndicator,
    dragHandleProps,
    selectedGoalId,
    onPressGroupHeader,
    onPressGoal,
    onPressGoalPercentage,
}: GoalGroupSectionProps) => {
    const syntheticPriority = getSyntheticGoalPriorityFromActual(
        group.priority ?? 0,
        moneyMap,
    );

    const sortedGroupRules = useMemo(
        () => sortBy(groupRules, 'priority'),
        [groupRules],
    );

    const groupGoalIds = sortedGroupRules.map((r) => r.id ?? '');

    const innerCornerRadius = isDragOverlay ? 16 : 16 - GOAL_DRAG_STROKE_PX;

    return (
        <YStack
            width="100%"
            backgroundColor="white"
            borderRadius={16}
            overflow={isDragOverlay ? 'hidden' : 'visible'}
            cursor={isDragOverlay ? 'grabbing' : 'default'}
            borderWidth={isDragOverlay ? 0 : GOAL_DRAG_STROKE_PX}
            borderColor={
                !isDragOverlay && showOutline ? DRAG_PINK : 'transparent'
            }
            borderStyle="solid"
            style={{ boxSizing: 'border-box' }}
        >
            <XStack
                position="relative"
                zIndex={1}
                backgroundColor="$white"
                borderTopLeftRadius={innerCornerRadius}
                borderTopRightRadius={innerCornerRadius}
                padding={16}
                minHeight={80}
                alignItems="center"
                gap={12}
                cursor={
                    !isDragOverlay && onPressGroupHeader != null
                        ? 'pointer'
                        : 'default'
                }
                onPress={!isDragOverlay ? onPressGroupHeader : undefined}
            >
                <MoneyMapRulePriority priority={syntheticPriority} />
                <YStack flex={1} minWidth={0} gap={2}>
                    <BodyMediumRegular numberOfLines={1}>
                        {group.name}
                    </BodyMediumRegular>
                    {group.description != null &&
                        group.description.length > 0 && (
                            <BodySmallRegular
                                color="$gray1000"
                                numberOfLines={2}
                            >
                                {group.description}
                            </BodySmallRegular>
                        )}
                </YStack>
                {dragHandleProps != null && <DragHandle {...dragHandleProps} />}
            </XStack>

            {sortedGroupRules.length > 0 && (
                <YStack
                    position="relative"
                    zIndex={0}
                    paddingHorizontal={16}
                    paddingBottom={16}
                    borderBottomLeftRadius={innerCornerRadius}
                    borderBottomRightRadius={innerCornerRadius}
                >
                    {isDragOverlay ? (
                        sortedGroupRules.map((rule) => (
                            <YStack key={rule.id}>
                                <GroupGoalRow rule={rule} />
                            </YStack>
                        ))
                    ) : (
                        <SortableContext
                            items={groupGoalIds}
                            strategy={verticalListSortingStrategy}
                        >
                            {sortedGroupRules.map((rule) => (
                                <SortableGroupGoal
                                    key={rule.id}
                                    rule={rule}
                                    groupId={group.id ?? ''}
                                    dropIndicator={dropIndicator}
                                    isSelected={
                                        selectedGoalId != null &&
                                        selectedGoalId === rule.id
                                    }
                                    onPress={
                                        onPressGoal != null && rule.id != null
                                            ? () => {
                                                  const goalId = rule.id;
                                                  if (goalId == null) return;
                                                  onPressGoal(goalId);
                                              }
                                            : undefined
                                    }
                                    onPressPercentage={
                                        onPressGoalPercentage != null &&
                                        rule.id != null
                                            ? () => {
                                                  const goalId = rule.id;
                                                  if (goalId == null) return;
                                                  onPressGoalPercentage(goalId);
                                              }
                                            : undefined
                                    }
                                />
                            ))}
                        </SortableContext>
                    )}
                </YStack>
            )}
        </YStack>
    );
};
